/**
 * Settings -> Rewards: the rule-builder.
 *
 * Lists the reward rules on the tenant's claim workflow and lets an admin add or
 * edit them. A state can carry MORE THAN ONE rule (per-product / per-value):
 * each rule is named, optionally gated by a condition, and issues either a fixed
 * amount or a percentage of a claim field, to a chosen account / reward / email
 * template. The condition editor is the shared <ConditionBuilder> (same one used
 * by validation / approval / field-visibility / email-trigger rules), so a rule
 * can gate on ANY claim field (incl. address components) with any operator -
 * nothing reward-specific. Gated by the `rewards` flag at the route.
 */

import { useEffect, useMemo, useState } from 'react'
import { Gift, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { toastApiError } from '@/shared/lib/api-error'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import {
  predicateToBuilder,
  VALUELESS_OPS,
  type Op,
  type PredicateNode,
} from '@/admin/components/condition-builder/types'
import { useForm, type FormField } from '@/shared/lib/forms'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useRemoveRewardRule,
  useRewardRoutingOptions,
  useRewardRules,
  useUpsertRewardRule,
} from '@/admin/hooks/use-integrations'
import type { RewardRule, RewardTriggerOption } from '@/admin/types/integrations'
import { USE_DEFAULT, compileConfig, ruleToFormValues } from './rewards-rules-helpers'

const TRIGGER_SEP = '::'

/** Claim-scoped ref lookup powering condition summaries + the builder. */
type RefLookup = (ref: string) => { label: string } | undefined

interface DialogState {
  open: boolean
  editing: RewardRule | null
}

export function RewardRulesTab() {
  const { data, isLoading } = useRewardRules()
  const upsert = useUpsertRewardRule()
  const remove = useRemoveRewardRule()
  const { byRef } = useAvailableRefs({ entityKey: 'claim' })
  const { can } = usePermissions()
  // Authoring reward rules (add / edit / delete) is gated by rewards.rules;
  // rewards.view alone is read-only. Only admins hold rewards.rules today.
  const canManageRules = can('rewards.rules')
  const [dialog, setDialog] = useState<DialogState>({ open: false, editing: null })
  const [ruleToDelete, setRuleToDelete] = useState<RewardRule | null>(null)

  const rules = data?.rules ?? []
  const triggerOptions = data?.trigger_options ?? []

  const close = () => setDialog({ open: false, editing: null })

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-[24px] text-[var(--ink)]">Reward rules</h1>
          <p className="text-[13px] text-[var(--ink-3)]">
            Decide when a claim issues a reward. A state can have several rules -
            each can apply only when claim fields match, and pay a fixed amount
            or a percentage of a claim field.
          </p>
        </div>
        {canManageRules && (
          <Button
            onClick={() => setDialog({ open: true, editing: null })}
            disabled={triggerOptions.length === 0}
          >
            <Plus className="h-3.5 w-3.5" />
            Add reward rule
          </Button>
        )}
      </header>

      {isLoading ? (
        <p className="text-[13px] text-[var(--ink-3)]">Loading rules...</p>
      ) : rules.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-6 py-10 text-center">
          <Gift className="h-5 w-5 text-[var(--ink-3)]" />
          <p className="text-[13px] text-[var(--ink-2)]">No reward rules yet.</p>
          <p className="text-[12px] text-[var(--ink-3)]">
            Add a rule to issue a reward when a claim reaches a state.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rules.map((rule) => (
            <li
              key={`${rule.workflow_key}:${rule.state_key}:${rule.key}`}
              className="flex items-center justify-between gap-4 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3"
            >
              <div className="space-y-0.5">
                <p className="text-[13px] text-[var(--ink)]">{rule.label}</p>
                <p className="text-[12px] text-[var(--ink-3)]">
                  When a claim reaches &ldquo;{rule.state_label}&rdquo;
                  {summariseCondition(rule.when, byRef)}
                </p>
                <p className="text-[12px] text-[var(--ink-3)]">
                  {summariseReward(rule.config, byRef)}
                </p>
              </div>
              {canManageRules && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Edit rule ${rule.label}`}
                    onClick={() => setDialog({ open: true, editing: rule })}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete rule ${rule.label}`}
                    onClick={() => setRuleToDelete(rule)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog.open && (
        <RuleDialog
          key={
            dialog.editing
              ? `${dialog.editing.workflow_key}:${dialog.editing.state_key}:${dialog.editing.key}`
              : 'add'
          }
          editing={dialog.editing}
          triggerOptions={triggerOptions}
          pending={upsert.isPending}
          onClose={close}
          onSubmit={async (payload) => {
            try {
              await upsert.mutateAsync(payload)
              toast.success('Reward rule saved')
              close()
            } catch (err: unknown) {
              toastApiError(err, 'Failed to save reward rule')
            }
          }}
        />
      )}

      <ConfirmDeleteDialog
        open={ruleToDelete !== null}
        onOpenChange={(next) => !next && setRuleToDelete(null)}
        title="Delete reward rule?"
        description={
          ruleToDelete
            ? `"${ruleToDelete.label}" will stop issuing rewards when a claim reaches "${ruleToDelete.state_label}".`
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={() => {
          if (!ruleToDelete) return
          remove.mutate(
            {
              workflow_key: ruleToDelete.workflow_key,
              state_key: ruleToDelete.state_key,
              key: ruleToDelete.key,
            },
            {
              onSuccess: () => setRuleToDelete(null),
              onError: (err) => toastApiError(err, 'Failed to delete rule'),
            },
          )
        }}
      />
    </div>
  )
}

interface RuleDialogProps {
  editing: RewardRule | null
  triggerOptions: RewardTriggerOption[]
  pending: boolean
  onClose: () => void
  onSubmit: (payload: {
    workflow_key: string
    state_key: string
    key?: string
    label: string
    config: Record<string, unknown>
    when?: unknown
  }) => void
}

interface Captured {
  workflow_key: string
  state_key: string
  label: string
}

function RuleDialog({
  editing,
  triggerOptions,
  pending,
  onClose,
  onSubmit,
}: RuleDialogProps) {
  const isEdit = editing !== null
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs({
    entityKey: 'claim',
  })
  // Dropdown data is a live provider read - fetched only now that the dialog is
  // open (cached across reopens), so the rules list + save stay off the provider.
  const {
    data: routing,
    isLoading: routingLoading,
    isError: routingError,
  } = useRewardRoutingOptions()
  const { accounts, catalog, templates } = useMemo(
    () => ({
      accounts: routing?.accounts ?? [],
      catalog: routing?.catalog ?? [],
      templates: routing?.templates ?? [],
    }),
    [routing],
  )
  const optionsLoading = refsLoading || routingLoading
  const [step, setStep] = useState<'trigger' | 'configure'>(
    isEdit ? 'configure' : 'trigger',
  )
  const [captured, setCaptured] = useState<Captured | null>(
    editing
      ? {
          workflow_key: editing.workflow_key,
          state_key: editing.state_key,
          label: editing.label,
        }
      : null,
  )
  // The condition predicate lives outside the generated form (it is the shared
  // ConditionBuilder, not a generated field); the submit handler reads it.
  const [when, setWhen] = useState<PredicateNode | null>(
    (editing?.when as PredicateNode | null) ?? null,
  )

  // Re-sync when the dialog is reused (the key prop usually remounts, but be safe).
  useEffect(() => {
    setStep(isEdit ? 'configure' : 'trigger')
    setCaptured(
      editing
        ? {
            workflow_key: editing.workflow_key,
            state_key: editing.state_key,
            label: editing.label,
          }
        : null,
    )
    setWhen((editing?.when as PredicateNode | null) ?? null)
  }, [editing, isEdit])

  // ── Step 1 (add only): name + trigger ─────────────────────────────
  const triggerFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Rule name',
        placeholder: 'e.g. Premium products',
        value: '',
        validationSchema: z.string().trim().min(1, 'Name the rule.'),
      },
      {
        type: 'select',
        name: 'trigger',
        label: 'When a claim reaches',
        value: '',
        options: triggerOptions.map((o) => ({
          label: o.state_label,
          value: `${o.workflow_key}${TRIGGER_SEP}${o.state_key}`,
        })),
        validationSchema: z.string().min(1, 'Pick a trigger state.'),
      },
    ],
    [triggerOptions],
  )

  const { Form: TriggerForm } = useForm(
    { name: 'reward-trigger-form', submit: 'footer', fields: triggerFields },
    async (raw) => {
      const values = raw as Record<string, string>
      const [workflow_key, state_key] = values.trigger.split(TRIGGER_SEP)
      setCaptured({ workflow_key, state_key, label: values.label.trim() })
      setStep('configure')
    },
    [triggerOptions],
  )

  // ── Step 2: reward config (+ shared condition builder) ────────────
  // Numeric claim fields drive the "percentage of a claim field" amount mode;
  // sourced from the shared ref catalog (entity:claim.* etc.), same place the
  // ConditionBuilder draws from - no reward-specific field list.
  const numericOptions = useMemo(
    () =>
      refs
        // amount_from is resolved on the claim itself (issue_reward reads claim
        // columns/EAV), so only offer claim-own number fields - not the
        // related-entity refs the builder also surfaces for conditions, which
        // would never resolve at issue time.
        .filter((r) => r.type === 'number' && r.ref.startsWith('entity:claim.'))
        .map((r) => ({ label: r.label, value: r.ref })),
    [refs],
  )

  const initial = useMemo(() => ruleToFormValues(editing?.config), [editing])

  const configureFields = useMemo<FormField[]>(() => {
    const list: FormField[] = []
    if (isEdit) {
      list.push({
        type: 'text',
        name: 'label',
        label: 'Rule name',
        value: editing?.label ?? '',
        validationSchema: z.string().trim().min(1, 'Name the rule.'),
      })
    }
    list.push(
      {
        type: 'select',
        name: 'amount_mode',
        label: 'Reward amount',
        value: initial.amount_mode,
        options: [
          { label: 'Fixed amount', value: 'fixed' },
          ...(numericOptions.length
            ? [
                { label: 'Amount entered on the claim', value: 'claim_field' },
                { label: 'Percentage of a claim field', value: 'percent' },
              ]
            : []),
        ],
      },
      {
        type: 'text',
        name: 'amount',
        label: 'Amount',
        placeholder: 'e.g. 50',
        value: initial.amount,
        visible: (ctx) => ctx.values.amount_mode === 'fixed',
        validationSchema: z
          .string()
          .trim()
          .refine((v) => v === '' || Number(v) > 0, 'Enter a positive amount.'),
      },
      {
        type: 'select',
        name: 'amount_field',
        label: 'Claim field (holds the amount)',
        value: initial.amount_field,
        options: numericOptions,
        visible: (ctx) =>
          ctx.values.amount_mode === 'claim_field' ||
          ctx.values.amount_mode === 'percent',
      },
      {
        type: 'text',
        name: 'amount_percent',
        label: 'Percentage (e.g. 10 for 10%)',
        placeholder: 'e.g. 10',
        value: initial.amount_percent,
        visible: (ctx) => ctx.values.amount_mode === 'percent',
        validationSchema: z
          .string()
          .trim()
          .refine(
            (v) => v === '' || (Number(v) > 0 && Number(v) <= 100),
            'Enter a percentage from 1 to 100.',
          ),
      },
    )
    list.push(
      catalog.length
        ? {
            type: 'select',
            name: 'utid',
            label: 'Reward',
            value: initial.utid,
            options: [
              { label: 'Use connected default', value: USE_DEFAULT },
              ...catalog.map((r) => ({
                label: r.currency
                  ? `${r.reward_name || r.utid} (${r.currency})`
                  : r.reward_name || r.utid,
                value: r.utid,
              })),
            ],
          }
        : {
            type: 'text',
            name: 'utid',
            label: 'Reward utid (optional)',
            placeholder: 'Optional',
            value: initial.utid === USE_DEFAULT ? '' : initial.utid,
          },
      accounts.length
        ? {
            type: 'select',
            name: 'account_identifier',
            label: 'Account',
            value: initial.account_identifier,
            options: [
              { label: 'Use connected default', value: USE_DEFAULT },
              ...accounts.map((a) => {
                // Surface the account identifier (like the template etid) so
                // near-identical account names stay distinguishable.
                const detail = [
                  a.display_name ? a.account_identifier : '',
                  a.currency_code,
                ]
                  .filter(Boolean)
                  .join(', ')
                const name = a.display_name || a.account_identifier
                return {
                  label: detail ? `${name} (${detail})` : name,
                  value: a.account_identifier,
                }
              }),
            ],
          }
        : {
            type: 'text',
            name: 'account_identifier',
            label: 'Account identifier (optional)',
            placeholder: 'Optional',
            value:
              initial.account_identifier === USE_DEFAULT
                ? ''
                : initial.account_identifier,
          },
      templates.length
        ? {
            type: 'select',
            name: 'etid',
            label: 'Email template',
            value: initial.etid,
            options: [
              { label: 'Use connected default', value: USE_DEFAULT },
              ...templates.map((t) => ({
                label: t.name ? `${t.name} (${t.etid})` : t.etid,
                value: t.etid,
              })),
            ],
          }
        : {
            type: 'text',
            name: 'etid',
            label: 'Email template ID (optional)',
            placeholder: 'e.g. E400466',
            value: initial.etid === USE_DEFAULT ? '' : initial.etid,
          },
      {
        type: 'text',
        name: 'message',
        label: 'Message (optional)',
        placeholder: 'Optional note on the reward email',
        value: initial.message,
      },
    )
    return list
  }, [numericOptions, initial, isEdit, editing, accounts, catalog, templates])

  const { Form: ConfigureForm } = useForm(
    { name: 'reward-configure-form', submit: 'footer', fields: configureFields },
    async (raw) => {
      if (!captured) return
      const values = raw as Record<string, string>
      const config = compileConfig(values)
      // The amount must be fully specified for the chosen mode - there is no
      // hidden account-default fallback, so an admin always knows what fires.
      if (values.amount_mode === 'fixed' && config.amount == null) {
        toast.error('Enter a reward amount.')
        return
      }
      if (
        (values.amount_mode === 'claim_field' ||
          values.amount_mode === 'percent') &&
        config.amount_from == null
      ) {
        toast.error('Pick the claim field that holds the amount.')
        return
      }
      if (values.amount_mode === 'percent' && config.amount_percent == null) {
        toast.error('Enter the percentage.')
        return
      }
      // Currency follows the chosen reward (USD reward -> USD, CAD -> CAD).
      const reward = catalog.find((r) => r.utid === values.utid)
      if (reward?.currency) config.currency = reward.currency
      onSubmit({
        workflow_key: captured.workflow_key,
        state_key: captured.state_key,
        key: editing ? editing.key : undefined,
        label: isEdit ? values.label?.trim() || captured.label : captured.label,
        when: when ?? undefined,
        config,
      })
    },
    [captured, configureFields, editing, catalog, when, onSubmit],
  )

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[640px] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit reward rule' : 'Add reward rule'}</DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            {isEdit
              ? `When a claim reaches "${editing?.state_label ?? ''}".`
              : step === 'trigger'
                ? 'Name the rule and pick the claim state that issues the reward.'
                : 'Choose what to issue, and optionally limit the rule to matching claims.'}
          </DialogDescription>
        </DialogHeader>

        {!isEdit && step === 'trigger' ? (
          <>
            <TriggerForm className="space-y-5" />
            <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" form="reward-trigger-form">
                Next
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {optionsLoading ? (
              <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
                Loading options...
              </p>
            ) : (
              <>
                {routingError && (
                  <p className="rounded-[4px] border-[0.5px] border-[var(--status-error-bg)] bg-[var(--status-error-bg)] px-3 py-2 text-[12px] text-[var(--status-error)]">
                    Could not load reward options - you can still enter the
                    reward, account, and template IDs manually below.
                  </p>
                )}
                <ConfigureForm className="space-y-5" />
                <div className="flex flex-col gap-1.5 pt-1">
                  <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Only when (optional)
                  </Label>
                  <ConditionBuilder
                    value={when}
                    onChange={setWhen}
                    availableRefs={refs}
                    byRef={byRef}
                  />
                </div>
              </>
            )}
            <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
              {isEdit ? (
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setStep('trigger')}>
                  Back
                </Button>
              )}
              <Button
                type="submit"
                form="reward-configure-form"
                disabled={pending || optionsLoading}
              >
                Save rule
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

const OP_LABEL: Partial<Record<Op, string>> = {
  eq: 'is',
  ne: 'is not',
  in: 'is one of',
  not_in: 'is not one of',
  gt: 'greater than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  matches: 'matches',
  exists: 'is set',
}

function stringifyClauseValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ')
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

/** Human summary of a rule's `when` predicate for the list row. */
function summariseCondition(when: unknown, byRef: RefLookup): string {
  if (!when) return ''
  const clauses = predicateToBuilder(when).clauses.filter((c) => c.ref && c.op)
  if (!clauses.length) return ''
  const text = clauses
    .map((c) => {
      const label = byRef(c.ref)?.label ?? c.ref.split('.').pop() ?? c.ref
      const op = OP_LABEL[c.op] ?? c.op
      return VALUELESS_OPS.has(c.op)
        ? `${label} ${op}`
        : `${label} ${op} ${stringifyClauseValue(c.value)}`.trim()
    })
    .join(' and ')
  return ` - when ${text}`
}

function summariseReward(
  config: Record<string, unknown>,
  byRef: RefLookup,
): string {
  if (config.amount_from) {
    const ref = String(config.amount_from)
    const base = byRef(ref)?.label ?? ref.split('.').pop() ?? ref
    const pct = config.amount_percent
    return pct != null && pct !== ''
      ? `Reward: ${pct}% of ${base}`
      : `Reward: amount from ${base}`
  }
  const parts: string[] = []
  if (config.amount != null && config.amount !== '') parts.push(String(config.amount))
  if (config.currency) parts.push(String(config.currency).toUpperCase())
  if (config.utid) parts.push(`utid ${String(config.utid)}`)
  return parts.length ? `Reward: ${parts.join(' ')}` : 'Uses the connected account defaults'
}
